import React, { useCallback } from 'react';

import { ChatIcon, HomeIcon, SearchIcon, RovoIcon } from '@atlaskit/logo';
import { defineMessage, useIntl } from 'react-intl';

import { AnalyticsTopNavItem, useSendTopNavItemClick } from '@townsquare/analytics-nav4';
import { GLOBAL_ROVO_DISPLAY_NAME } from '@townsquare/config/constants';
import { useRouteDependencies } from '@townsquare/router/primitives/dependencies';
import { generatePath } from '@townsquare/router/primitives/path';
import { chatRoute } from '@townsquare/routes/chat';
import { homeTabRoute } from '@townsquare/routes/hometab-view';
import { rovoSearchRoute } from '@townsquare/routes/rovo-search-view';
import { useRovoHomeBranding, type RovoHomeBranding } from '@townsquare/rovo/home-branding';

import { useIsOnFullscreenChat, useIsOnFullscreenSearch, useIsOnRovoSearch } from '../router/hooks';

const productHomeLabelMessage = defineMessage({
	id: 'townsquare.navigation.chrome-navigation.nav-logo-label',
	defaultMessage: 'Home page',
	description:
		'Accessible label for the product logo link in the top left of the navigation which takes users to the product home page',
});

const RovoProductIcon = () => <RovoIcon size="small" testId="rovo-icon" label="" />;
const ChatProductIcon = () => <ChatIcon size="small" testId="chat-icon" label="" />;
const SearchProductIcon = () => <SearchIcon size="small" testId="search-icon" label="" />;
const HomeProductIcon = () => <HomeIcon size="small" testId="home-icon" label="" />;

function getProductHomeDetails(
	isChatRoute: boolean,
	isSearchRoute: boolean,
	rovoHomeBranding: RovoHomeBranding | undefined,
) {
	if (rovoHomeBranding) {
		return {
			Icon: rovoHomeBranding.icon === 'rovo' ? RovoProductIcon : HomeProductIcon,
			route: homeTabRoute,
			name: rovoHomeBranding.label === 'rovo-home' ? GLOBAL_ROVO_DISPLAY_NAME : 'Home',
		};
	} else if (isChatRoute) {
		return { Icon: ChatProductIcon, route: chatRoute, name: 'Chat' };
	} else if (isSearchRoute) {
		return { Icon: SearchProductIcon, route: rovoSearchRoute, name: 'Search' };
	} else {
		return { Icon: HomeProductIcon, route: homeTabRoute, name: 'Home' };
	}
}

export type ProductHomeDetails = {
	Icon: () => React.JSX.Element;
	name: string;
	href: string;
	label: string;
	onClick: () => void;
};

export function useProductHomeDetails(): ProductHomeDetails {
	const sendTopNavItemClick = useSendTopNavItemClick();
	const intl = useIntl();
	const routeDependencies = useRouteDependencies();
	const isOnFullscreenChat = useIsOnFullscreenChat();
	const isChatRoute = !!isOnFullscreenChat;
	const isOnFullscreenSearch = useIsOnFullscreenSearch();
	const isOnRovoSearch = useIsOnRovoSearch();
	const isSearchRoute = isOnFullscreenSearch || isOnRovoSearch;
	const rovoHomeBranding = useRovoHomeBranding();

	const { Icon, name, route } = getProductHomeDetails(isChatRoute, isSearchRoute, rovoHomeBranding);

	const onClick = useCallback(() => {
		void sendTopNavItemClick(AnalyticsTopNavItem.PRODUCT_LOGO);
	}, [sendTopNavItemClick]);

	return {
		Icon,
		name,
		href: generatePath(route, routeDependencies),
		label: intl.formatMessage(productHomeLabelMessage),
		onClick,
	};
}
